import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { deletePoll, getPolls, getPollSends, setPollStatus } from '../Api';
import type { PollDto, PollSendDto } from '../models/dtos/PollDto';
import {
    formatDateTime,
    PollSendDialog,
    PollSendsTable,
    PollStatusChip,
    PollSummaryLine,
} from '../components/polls/PollShared';

type PageMessage = { type: 'success' | 'error'; text: string } | null;

const SENDS_PAGE_SIZE = 25;

export default function PollsPage() {
    const navigate = useNavigate();
    const [tab, setTab] = React.useState<'polls' | 'sends'>('polls');
    const [loading, setLoading] = React.useState(true);
    const [message, setMessage] = React.useState<PageMessage>(null);
    const [polls, setPolls] = React.useState<PollDto[]>([]);
    const [sends, setSends] = React.useState<PollSendDto[]>([]);
    const [sendsTotal, setSendsTotal] = React.useState(0);
    const [sendsOffset, setSendsOffset] = React.useState(0);
    const [sendTarget, setSendTarget] = React.useState<PollDto | null>(null);

    const loadPolls = React.useCallback(async () => {
        const list = await getPolls();
        setPolls(list ?? []);
    }, []);

    const loadSends = React.useCallback(async (offset: number) => {
        const page = await getPollSends(null, offset, SENDS_PAGE_SIZE);
        setSends(page?.items ?? []);
        setSendsTotal(page?.totalItems ?? 0);
    }, []);

    React.useEffect(() => {
        (async () => {
            setLoading(true);
            await Promise.all([loadPolls(), loadSends(0)]);
            setLoading(false);
        })();
    }, [loadPolls, loadSends]);

    // Refresh while a send runs so its counters move.
    const running = sends.some(s => s.status === 'PENDING' || s.status === 'SENDING');
    React.useEffect(() => {
        if (!running) return;
        const timer = window.setInterval(() => {
            loadSends(sendsOffset);
            loadPolls();
        }, 3000);
        return () => window.clearInterval(timer);
    }, [running, sendsOffset, loadSends, loadPolls]);

    const changeStatus = async (poll: PollDto, status: 'ACTIVE' | 'CLOSED') => {
        if (status === 'CLOSED' && !window.confirm(`Close “${poll.name}”? Players who open it afterwards see that it has ended.`)) return;
        const result = await setPollStatus(poll.id, status);
        if (!result.ok) {
            setMessage({ type: 'error', text: result.error });
            return;
        }
        setMessage({ type: 'success', text: status === 'CLOSED' ? 'Poll closed.' : 'Poll reopened.' });
        await loadPolls();
    };

    const remove = async (poll: PollDto) => {
        if (!window.confirm(`Delete “${poll.name}”?`)) return;
        const result = await deletePoll(poll.id);
        if (!result.ok) {
            setMessage({ type: 'error', text: result.error });
            return;
        }
        setMessage({ type: 'success', text: 'Poll deleted.' });
        await loadPolls();
    };

    const onSent = async (send: PollSendDto) => {
        setSendTarget(null);
        setMessage({ type: 'success', text: `Sending “${send.pollName ?? 'poll'}” — progress is in Send history.` });
        setTab('sends');
        setSendsOffset(0);
        await Promise.all([loadPolls(), loadSends(0)]);
    };

    return (
        <Box sx={{ mx: 3, my: 3 }}>
            <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
                    <Typography variant="h4">Polls</Typography>
                    <Button variant="contained" onClick={() => navigate('/marketing/polls/new')}>New poll</Button>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    Ask players a question by push. Tapping it opens the poll in the app: they pick an option or write their own
                    answer; closing it is recorded as a refusal. Texts follow each player's profile language.
                </Typography>
                {message && <Alert severity={message.type} onClose={() => setMessage(null)}>{message.text}</Alert>}

                <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab value="polls" label={`Polls (${polls.length})`} />
                    <Tab value="sends" label="Send history" />
                </Tabs>

                {loading ? (
                    <CircularProgress />
                ) : tab === 'polls' ? (
                    <Stack spacing={2}>
                        {polls.length === 0 && (
                            <Typography variant="body2" color="text.secondary">No polls yet.</Typography>
                        )}
                        {polls.map(poll => {
                            const question = poll.texts[poll.defaultLanguage]?.question ?? '';
                            const languages = Object.keys(poll.texts);
                            return (
                                <Card key={poll.id} variant="outlined">
                                    <CardContent>
                                        <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                                            <Typography variant="h6">{poll.name}</Typography>
                                            <PollStatusChip status={poll.status} />
                                        </Stack>
                                        <Typography variant="body1" sx={{ mb: 1 }}>{question}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {poll.options.length} option{poll.options.length === 1 ? '' : 's'}
                                            {poll.allowCustomAnswer ? ' + custom answer' : ''} · {languages.map(l => l.toUpperCase()).join(', ')}
                                            {' · '}{poll.sendsCount} send{poll.sendsCount === 1 ? '' : 's'}
                                            {poll.lastSentAt ? ` · last sent ${formatDateTime(poll.lastSentAt)}` : ''}
                                        </Typography>
                                        <PollSummaryLine summary={poll.summary} />
                                    </CardContent>
                                    <CardActions>
                                        <Button size="small" onClick={() => navigate('/marketing/polls/' + encodeURIComponent(poll.id) + '/edit')}>
                                            Edit
                                        </Button>
                                        <Button size="small" onClick={() => navigate('/marketing/polls/' + encodeURIComponent(poll.id) + '/stats')}>
                                            Stats
                                        </Button>
                                        {poll.status !== 'CLOSED' && (
                                            <Button size="small" variant="contained" onClick={() => setSendTarget(poll)}>Send</Button>
                                        )}
                                        {poll.status === 'ACTIVE' && (
                                            <Button size="small" color="warning" onClick={() => changeStatus(poll, 'CLOSED')}>Close</Button>
                                        )}
                                        {poll.status === 'CLOSED' && (
                                            <Button size="small" onClick={() => changeStatus(poll, 'ACTIVE')}>Reopen</Button>
                                        )}
                                        {poll.status === 'DRAFT' && (
                                            <Button size="small" color="error" onClick={() => remove(poll)}>Delete</Button>
                                        )}
                                    </CardActions>
                                </Card>
                            );
                        })}
                    </Stack>
                ) : (
                    <Stack spacing={2}>
                        <PollSendsTable sends={sends} showPoll />
                        {sendsTotal > SENDS_PAGE_SIZE && (
                            <Stack direction="row" gap={1} alignItems="center">
                                <Button
                                    size="small"
                                    disabled={sendsOffset === 0}
                                    onClick={() => {
                                        const next = Math.max(0, sendsOffset - SENDS_PAGE_SIZE);
                                        setSendsOffset(next);
                                        loadSends(next);
                                    }}
                                >
                                    Newer
                                </Button>
                                <Typography variant="body2" color="text.secondary">
                                    {sendsOffset + 1}–{Math.min(sendsOffset + SENDS_PAGE_SIZE, sendsTotal)} of {sendsTotal}
                                </Typography>
                                <Button
                                    size="small"
                                    disabled={sendsOffset + SENDS_PAGE_SIZE >= sendsTotal}
                                    onClick={() => {
                                        const next = sendsOffset + SENDS_PAGE_SIZE;
                                        setSendsOffset(next);
                                        loadSends(next);
                                    }}
                                >
                                    Older
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                )}
            </Stack>
            <PollSendDialog poll={sendTarget} open={sendTarget != null} onClose={() => setSendTarget(null)} onSent={onSent} />
        </Box>
    );
}
